import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import project from '@data/project.json';
import unitsData from '@data/units.json';
import marketData from '@data/market.json';
import poisData from '@data/pois.json';
import Step1Welcome from '@steps/Step1Welcome';
import Step2Project from '@steps/Step2Project';
import Step3Goal from '@steps/Step3Goal';
import Step4Match from '@steps/Step4Match';
import Step5Guarantees from '@steps/Step5Guarantees';
import Step6Report from '@steps/Step6Report';
import SharedReport from '@steps/SharedReport';
import WelcomeSplash from '@components/WelcomeSplash';

// V3 D4 Phase A — V2 pages are lazy so v1 visitors don't pay for the
// strangler bundle. Each page is its own chunk; the shared shell +
// progress nav land in the chunk of whichever page loads first.
const V2WelcomePage    = lazy(() => import('@pages/v2/V2WelcomePage'));
// V3 D6.1 — print-only route /r2/:sessionId. Lazy so the heavy
// print bundle (Cairo + Inter + 6 page components) is only fetched
// by the headless browser inside Cloudflare Browser Rendering.
const V2ReportPrintPage = lazy(() => import('@pages/print/V2ReportPrintPage'));
const V2LocationPage   = lazy(() => import('@pages/v2/V2LocationPage'));
const V2ProjectPage    = lazy(() => import('@pages/v2/V2ProjectPage'));
const V2ApartmentsPage = lazy(() => import('@pages/v2/V2ApartmentsPage'));
const V2FinancePage    = lazy(() => import('@pages/v2/V2FinancePage'));
const V2ReportPage     = lazy(() => import('@pages/v2/V2ReportPage'));

// === Diagnostic Home — shows everything is wired correctly ===
function Home() {
  const [counts] = useState({
    units: unitsData.units.length,
    market: marketData.length,
    pois: poisData.pois.length,
  });

  return (
    <div className="step-container">
      <header className="text-center mb-10">
        <div className="inline-flex items-center gap-3 px-4 py-2 bg-gold-50 rounded-full mb-4">
          <span className="w-2 h-2 bg-available rounded-full animate-pulse-slow" />
          <span className="text-sm font-medium text-navy">Day 0 — Scaffold ready</span>
        </div>
        <h1 className="text-5xl md:text-6xl font-bold text-navy mb-3">
          KEY2 <span className="gradient-gold">BCS v2</span>
        </h1>
        <p className="text-lg text-navy-600">{project.tagline_ar}</p>
        <p className="text-sm text-navy-400 mt-2">{project.location_ar}</p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        <div className="stat-card"><div className="stat-value">{project.totalUnits}</div><div className="stat-label">وحدة</div></div>
        <div className="stat-card"><div className="stat-value">{project.floors}</div><div className="stat-label">طوابق</div></div>
        <div className="stat-card"><div className="stat-value">{project.shops}</div><div className="stat-label">محل</div></div>
        <div className="stat-card"><div className="stat-value">{project.amenitiesCount}</div><div className="stat-label">مرفق</div></div>
      </section>

      <section className="card p-6 mb-8">
        <h2 className="text-xl font-bold text-navy mb-4 flex items-center gap-2">
          <span className="text-gold">✓</span> Data wired & ready
        </h2>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <DataPill label="Units" count={counts.units} expected={81} />
          <DataPill label="Market listings" count={counts.market} expected={239} />
          <DataPill label="POIs" count={counts.pois} expected={16} />
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold text-navy mb-4">The 6 Steps</h2>
        <div className="grid gap-3">
          {[
            { n: 1, path: '/welcome', title: 'الافتتاح', time: '1د' },
            { n: 2, path: '/project', title: 'المشروع', time: '4د' },
            { n: 3, path: '/goal', title: 'الهدف', time: '2د' },
            { n: 4, path: '/match', title: 'التوافق', time: '8د' },
            { n: 5, path: '/guarantees', title: 'الضمانات', time: '2د' },
            { n: 6, path: '/report', title: 'التقرير', time: '3د' },
          ].map(s => (
            <Link
              key={s.n}
              to={s.path}
              className="card p-4 flex items-center gap-4 hover:border-gold transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-navy text-gold flex items-center justify-center font-bold num-en">
                {s.n}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-navy">{s.title}</div>
              </div>
              <div className="text-sm text-navy-400 num-en">{s.time}</div>
            </Link>
          ))}
        </div>
      </section>

      <footer className="mt-12 text-center text-xs text-navy-400 num-en">
        v0.1.0 · KEY2 BCS Express · المفتاح العقارية
      </footer>
    </div>
  );
}

function DataPill({ label, count, expected }: { label: string; count: number; expected: number }) {
  const ok = count === expected;
  return (
    <div className={`p-3 rounded-lg border ${ok ? 'border-available bg-green-50' : 'border-sold bg-red-50'}`}>
      <div className="text-xs text-navy-600 mb-1">{label}</div>
      <div className="text-2xl font-bold num-en">
        {count}<span className="text-sm text-navy-400">/{expected}</span>
      </div>
      <div className={`text-xs mt-1 ${ok ? 'text-available' : 'text-sold'}`}>
        {ok ? '✓ matches v1' : '✗ mismatch'}
      </div>
    </div>
  );
}

/**
 * V3 Day 2.5 — splash gate. On `/`, return visitors with the
 * sessionStorage flag bypass the splash and land directly on
 * /welcome. The flag is set the moment they hit either splash CTA
 * (handled inside WelcomeSplash). The splash itself is reachable
 * again via the back-home link in Step1Welcome.
 *
 * `?force=splash` lets QA / Mefteh re-enter the splash without
 * clearing storage.
 */
function RootGate() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  // V3 D4 Phase A — opt-in v2 entry. `?v=2` redirects to /v2/ before
  // any seen-flag logic kicks in, so the strangler is reachable from
  // any v1 link by appending the param.
  if (params.get('v') === '2') return <Navigate to="/v2" replace />;
  const force = params.get('force') === 'splash';
  const seen = (() => {
    try {
      return sessionStorage.getItem('key2:hasSeenSplash') === '1';
    } catch {
      return false;
    }
  })();
  if (seen && !force) return <Navigate to="/welcome" replace />;
  return <WelcomeSplash />;
}

// V3 D4 Phase A — minimal placeholder while a lazy v2 page resolves.
function V2Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-50 text-navy-500 text-sm">
      …
    </div>
  );
}

export default function App() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <Routes>
      {/* V3 D6.6 (2026-05-08) — v2 is now the default surface for the
          showroom. The root path forwards into the v2 strangler;
          v1 stays reachable at /v1 (splash) and via the legacy step
          paths below for any active deep links / past PDFs. */}
      <Route path="/" element={<Navigate to="/v2" replace />} />
      <Route path="/v1" element={<RootGate />} />
      <Route path="/home" element={<Home />} />
      <Route path="/welcome" element={<Step1Welcome />} />
      <Route path="/project" element={<Step2Project />} />
      <Route path="/goal" element={<Step3Goal />} />
      <Route path="/match" element={<Step4Match />} />
      <Route path="/guarantees" element={<Step5Guarantees />} />
      <Route path="/report" element={<Step6Report />} />
      <Route path="/r/:sessionId" element={<SharedReport />} />
      {/* V3 D6.1 — v2 magazine PDF, hit by Browser Rendering only */}
      <Route
        path="/r2/:sessionId"
        element={
          <Suspense fallback={<V2Loading />}>
            <V2ReportPrintPage />
          </Suspense>
        }
      />

      {/* ── V3 D4 Phase A — V2 strangler routes (additive) ──── */}
      <Route
        path="/v2"
        element={
          <Suspense fallback={<V2Loading />}>
            <V2WelcomePage />
          </Suspense>
        }
      />
      <Route
        path="/v2/location"
        element={
          <Suspense fallback={<V2Loading />}>
            <V2LocationPage />
          </Suspense>
        }
      />
      <Route
        path="/v2/project"
        element={
          <Suspense fallback={<V2Loading />}>
            <V2ProjectPage />
          </Suspense>
        }
      />
      <Route
        path="/v2/apartments"
        element={
          <Suspense fallback={<V2Loading />}>
            <V2ApartmentsPage />
          </Suspense>
        }
      />
      <Route
        path="/v2/finance"
        element={
          <Suspense fallback={<V2Loading />}>
            <V2FinancePage />
          </Suspense>
        }
      />
      <Route
        path="/v2/report"
        element={
          <Suspense fallback={<V2Loading />}>
            <V2ReportPage />
          </Suspense>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
